import { useState } from "react";
import { api, API } from "@/lib/api";
import { Phone, Video, Users, Clock, Download, PhoneMissed } from "lucide-react";

export const humanDuration = (s = 0) => {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};

const when = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
const clockOf = (iso) => new Date(iso).toLocaleTimeString(undefined, { timeStyle: "short" });

// Per-room call log: who joined, how long it ran, and the translated transcript.
export const CallHistory = ({ calls, onJoinLive }) => {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(null);

  const open = async (callId) => {
    setBusy(callId);
    try {
      const { data } = await api.get(`/calls/${callId}`);
      setDetail(data);
    } finally {
      setBusy(null);
    }
  };

  const totalSeconds = calls.reduce((sum, c) => sum + (c.duration_seconds || 0), 0);
  const answered = calls.filter((c) => c.answered).length;

  if (detail) {
    const { call, entries, by_speaker: bySpeaker } = detail;
    return (
      <div className="border-b border-white/10 bg-panel p-4" data-testid="transcript-detail">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <p className="font-display text-sm font-bold tracking-tight">
            {call.video ? "Video call" : "Voice call"} · {when(call.started_at)}
          </p>
          <p className="text-xs text-ink-soft" data-testid="transcript-duration">
            <Clock className="me-1 inline h-3 w-3" />
            {call.live ? "In progress" : humanDuration(call.duration_seconds)}
            {call.ended_by ? ` · ended by ${call.ended_by}` : ""}
          </p>
        </div>
        <ul className="mt-2 flex flex-wrap gap-2 text-xs" data-testid="transcript-joins">
          {(call.joins || []).map((j, i) => (
            <li key={`${j.user_id}-${i}`} className="rounded-full bg-page px-3 py-1">
              {j.name || "Guest"} · joined {clockOf(j.at)}
              {bySpeaker?.[j.name] ? ` · ${bySpeaker[j.name]} lines` : ""}
            </li>
          ))}
        </ul>
        <ul className="cml-scroll mt-3 max-h-56 space-y-2 overflow-y-auto text-sm">
          {entries.map((e) => (
            <li key={e.entry_id} className="rounded-2xl bg-page px-4 py-2.5">
              <span className="text-xs font-semibold text-brand">
                {clockOf(e.created_at)} · {e.speaker_name} ({e.source_language})
              </span>
              <p dir="auto" className="mt-1">{e.display_text}</p>
              {e.display_text !== e.text && (
                <p dir="auto" className="mt-1 text-xs text-ink-soft">{e.text}</p>
              )}
            </li>
          ))}
          {entries.length === 0 && (
            <p className="text-sm text-ink-soft">
              No captions were captured — captions were off, or nobody spoke long enough.
            </p>
          )}
        </ul>
        <div className="mt-3 flex items-center gap-4">
          <button data-testid="back-to-calls" onClick={() => setDetail(null)} className="text-xs text-brand">
            ← Back to call history
          </button>
          <a
            data-testid={`download-transcript-${call.call_id}`}
            href={`${API}/calls/${call.call_id}/export`}
            className="inline-flex items-center gap-1.5 text-xs text-ink-soft hover:text-ink"
          >
            <Download className="h-3 w-3" /> Download transcript
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-white/10 bg-panel p-4" data-testid="transcripts-panel">
      {calls.length > 0 && (
        <p className="mb-3 text-xs text-ink-soft" data-testid="call-history-summary">
          {calls.length} {calls.length === 1 ? "call" : "calls"} · {answered} answered ·{" "}
          {humanDuration(totalSeconds)} on the line
        </p>
      )}
      <ul className="space-y-2 text-sm">
        {calls.map((c) => (
          <li
            key={c.call_id}
            data-testid={`call-row-${c.call_id}`}
            className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-page px-4 py-2.5"
          >
            <span className="flex min-w-0 items-center gap-3">
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  c.answered ? "bg-brand/15 text-brand" : "bg-white/10 text-ink-soft"
                }`}
              >
                {!c.answered ? (
                  <PhoneMissed className="h-3.5 w-3.5" />
                ) : c.video ? (
                  <Video className="h-3.5 w-3.5" />
                ) : (
                  <Phone className="h-3.5 w-3.5" />
                )}
              </span>
              <span className="min-w-0">
                <span className="block truncate font-medium">
                  {when(c.started_at)}
                  {c.live && (
                    <span className="ms-2 inline-flex items-center gap-1 rounded-full bg-brand/20 px-2 py-0.5 text-[10px] font-semibold text-brand">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand" /> Live
                    </span>
                  )}
                </span>
                <span className="block truncate text-xs text-ink-soft">
                  {c.live ? "In progress" : c.answered ? humanDuration(c.duration_seconds) : "No answer"} ·{" "}
                  <Users className="inline h-3 w-3" /> {(c.joins || []).map((j) => j.name).join(", ") ||
                    c.participants?.join(", ")}{" "}
                  · {c.entry_count || 0} lines
                </span>
              </span>
            </span>
            <span className="flex shrink-0 gap-2">
              {c.live && onJoinLive && (
                <button
                  data-testid={`history-join-${c.call_id}`}
                  onClick={() => onJoinLive(c)}
                  className="rounded-full bg-brand px-3 py-1 text-xs font-semibold text-white hover:bg-brand-hover"
                >
                  Join
                </button>
              )}
              <button
                data-testid={`open-transcript-${c.call_id}`}
                onClick={() => open(c.call_id)}
                className="rounded-full bg-brand/20 px-3 py-1 text-xs text-brand hover:bg-brand/30"
              >
                {busy === c.call_id ? "Opening…" : "Read"}
              </button>
              <a
                data-testid={`download-transcript-${c.call_id}`}
                href={`${API}/calls/${c.call_id}/export`}
                className="rounded-full bg-white/10 px-3 py-1 text-xs hover:bg-white/20"
              >
                Download
              </a>
            </span>
          </li>
        ))}
        {calls.length === 0 && (
          <p className="text-sm text-ink-soft">
            No calls yet — every call lands here with who joined, how long it ran and the translated transcript.
          </p>
        )}
      </ul>
    </div>
  );
};
